import React, { useMemo } from 'react';
import type { ScreenQuestion } from '../../../features/flow/types';
import {
  evaluateShowIf,
  isMetaAnswerKey,
  isPngFileName,
  normalizeFileAnswerValueForKey,
  prettyAnswerKey,
  uploadedFilesFromAnswers,
} from '../../../features/flow/utils';

function fileGroupLabel(answerKey: string): string {
  const dep = answerKey.match(/^dependent_(\d+)_(.+)$/);
  if (dep) {
    const idx = dep[1];
    const rest = dep[2];
    return `Dependent ${idx} ${prettyAnswerKey(rest)}`;
  }
  const partner = answerKey.match(/^partner_(.+)$/);
  if (partner) {
    return `Partner ${prettyAnswerKey(partner[1])}`;
  }
  const spouse = answerKey.match(/^spouse_(.+)$/);
  if (spouse) {
    return `Spouse ${prettyAnswerKey(spouse[1])}`;
  }
  return prettyAnswerKey(answerKey);
}

function isMyInfoUploadedFileKey(answerKey: string): boolean {
  // My Info uploads are typically tied to dependents/partner/spouse documents.
  // Exclude income/investment uploads (e.g. `investment_1_upload_broker_report`) from this review.
  return /^dependent_\d+_/.test(answerKey) || /^(partner_|spouse_)/.test(answerKey);
}

function isEmptyValue(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

function isDateLikeKey(key: string) {
  return /(date|dob|birth)/i.test(key);
}

function isIsoDateString(v: unknown) {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v.trim());
}

export function MyInfoReviewScreenBody({
  questions,
  answers,
  answersForShowIf,
  actionBusy,
  setAnswers,
}: {
  questions: ScreenQuestion[];
  answers: Record<string, unknown>;
  answersForShowIf: Record<string, any>;
  actionBusy: boolean;
  setAnswers: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
}) {
  const questionLabelByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const q of questions ?? []) {
      if (!evaluateShowIf(q.show_if, answersForShowIf)) continue;
      const key = (q.source_field ?? (q as any).sourceField ?? '').trim();
      if (!key) continue;
      const label = String(q.question_text ?? '').trim();
      if (!label) continue;
      map.set(key, label);
    }
    return map;
  }, [questions, answersForShowIf]);

  const sections = useMemo(() => {
    const labelForKey = (key: string) => questionLabelByKey.get(key) ?? prettyAnswerKey(key);

    const pushIfPresent = (out: Array<{ key: string; label: string }>, key: string, label?: string) => {
      if (!(key in (answers ?? {}))) return;
      const v = (answers ?? {})[key];
      if (isEmptyValue(v)) return;
      out.push({ key, label: label ?? labelForKey(key) });
    };

    const makeSection = (title: string, rows: Array<{ key: string; label: string }>) => ({
      title,
      rows: rows.filter((r) => !isMetaAnswerKey(r.key)),
    });

    // Compiled sections derived from `answers` keys (this screen is a summary of prior inputs).
    const personal: Array<{ key: string; label: string }> = [];
    [
      'first_name',
      'middle_name',
      'first_surname',
      'second_surname',
      'date_of_birth',
      'occupation',
      'email',
      'phone',
      // Common flow keys that may appear on "Introduce your data"
      'tax_id',
      'us_tax_id',
    ].forEach((k) => pushIfPresent(personal, k));

    const usTax: Array<{ key: string; label: string }> = [];
    // Include any us_* address fields present (not just a fixed subset)
    Object.keys(answers ?? {})
      .filter((k) => /^us_(tax_id|street_address|city|state|zip_code)/.test(k))
      .sort()
      .forEach((k) => pushIfPresent(usTax, k));

    const marital: Array<{ key: string; label: string }> = [];
    Object.keys(answers ?? {})
      .filter((k) => /^marital_/.test(k) || k === 'marital_status')
      .sort()
      .forEach((k) => pushIfPresent(marital, k));

    const partner: Array<{ key: string; label: string }> = [];
    Object.keys(answers ?? {})
      .filter((k) => /^(partner_|spouse_)/.test(k))
      .sort()
      .forEach((k) => pushIfPresent(partner, k));

    const dependents: Array<{ key: string; label: string }> = [];
    Object.keys(answers ?? {})
      .filter((k) => /^dependent_\d+_/.test(k))
      .filter((k) => !/^dependent_\d+_disability_certificate$/.test(k))
      .sort()
      .forEach((k) => pushIfPresent(dependents, k));

    const housing: Array<{ key: string; label: string }> = [];
    Object.keys(answers ?? {})
      .filter((k) => k === 'housing_situation' || /^housing_/.test(k) || /_mortgage_/.test(k))
      .sort()
      .forEach((k) => pushIfPresent(housing, k));

    return [
      makeSection('Your Personal Info', personal),
      makeSection('United State Tax ID & Address', usTax),
      makeSection('Marital Status', marital),
      makeSection('Partner Details', partner),
      makeSection('Dependents', dependents),
      makeSection('Housing Situations', housing),
    ].filter((s) => s.rows.length > 0);
  }, [answers, questionLabelByKey]);

  const uploadedFilesForReview = useMemo(
    () =>
      uploadedFilesFromAnswers(answers)
        .filter((row) => isMyInfoUploadedFileKey(row.answerKey))
        .map((row) => ({
          ...row,
          sectionName: /^dependent_\d+_/.test(row.answerKey) ? 'Dependents' : row.sectionName,
          groupLabel: fileGroupLabel(row.answerKey),
        }))
        .sort((a, b) => {
        const s = a.sectionName.localeCompare(b.sectionName);
        if (s !== 0) return s;
        const k = a.answerKey.localeCompare(b.answerKey);
        if (k !== 0) return k;
        return a.fileIndex - b.fileIndex;
      }),
    [answers]
  );

  return (
    <div className="w-full flex flex-col gap-10">
      {sections.map((sec, idx) => (
        <div key={sec.title} className="w-full">
          {idx > 0 ? <div className="w-full border-t border-[#E2E8F0] pt-10" /> : null}
          <div className="w-full max-w-[668px] pb-3">
            <div className="text-base font-medium text-[#131315]">{sec.title}:</div>
          </div>

          <div className="mt-6 w-full max-w-[668px] grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-8 items-start">
            {sec.rows.map(({ label, key }) => {
              const v = answers[key];
              const valStr = v === null || v === undefined ? '' : String(v);
              const isNum = typeof v === 'number';
              const isArr = Array.isArray(v);
              const isDate = isIsoDateString(v) || isDateLikeKey(key);

              return (
                <div key={key} className="min-w-0 col-span-1">
                  <div className="flex flex-col gap-3">
                    <label className="block text-sm text-[#131315] font-medium">{label}</label>

                    {isArr ? (
                      <textarea
                        className="w-full rounded-lg border border-[#E2E8F0] px-4 py-2 text-[#131315] bg-white focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB] disabled:opacity-50"
                        value={JSON.stringify(v)}
                        onChange={(e) => {
                          const raw = e.target.value;
                          try {
                            const parsed = JSON.parse(raw);
                            setAnswers((prev) => ({ ...prev, [key]: parsed }));
                          } catch {
                            setAnswers((prev) => ({ ...prev, [key]: raw }));
                          }
                        }}
                        rows={3}
                        disabled={actionBusy}
                      />
                    ) : (
                      <input
                        className="w-full rounded-lg border border-[#E2E8F0] px-4 py-2 text-[#131315] bg-white focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB] disabled:opacity-50"
                        type={isDate ? 'date' : isNum ? 'number' : 'text'}
                        value={valStr}
                        onChange={(e) =>
                          setAnswers((prev) => ({
                            ...prev,
                            [key]: isNum ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value,
                          }))
                        }
                        disabled={actionBusy}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {uploadedFilesForReview.length > 0 ? (
        <div className="w-full border-t border-[#E2E8F0] pt-10">
          <div className="w-full max-w-[668px] pb-3">
            <div className="text-sm font-semibold text-[#131315]">Uploaded Files:</div>
          </div>

          <div className="mt-4 w-full max-w-[668px] flex flex-col gap-3">
            {(() => {
              const seen = new Map<string, number>();
              return uploadedFilesForReview.map((row) => {
                const next = (seen.get(row.groupLabel) ?? 0) + 1;
                seen.set(row.groupLabel, next);
                return (
                  <div key={`${row.answerKey}_${row.fileName}_${row.fileIndex}`}>
                    <div className="text-xs text-[#525353] mb-2">{row.groupLabel}</div>
                    <div className="w-full max-w-[310px] rounded-lg border border-[#E2E8F0] bg-[#F9FAFB] px-4 py-3 flex items-center justify-between">
                      <span className="text-sm text-[#131315] break-all">
                        <span className="block text-base font-medium text-[#131315] mb-1">
                          File {next}: {row.fileName}
                        </span>
                      </span>
                      <button
                        type="button"
                        disabled={actionBusy}
                        onClick={() => {
                          setAnswers((prev) => {
                            const curVal = prev[row.answerKey];
                            const curAll = normalizeFileAnswerValueForKey(row.answerKey, curVal);
                            let pngSeen = -1;
                            const nextAll = curAll.filter((ref) => {
                              if (!isPngFileName(ref.name)) return true;
                              pngSeen += 1;
                              if (pngSeen === row.fileIndex && ref.name === row.fileName) return false;
                              return true;
                            });
                            return { ...prev, [row.answerKey]: nextAll.length > 0 ? nextAll : null };
                          });
                        }}
                        className="text-base font-medium text-[#131315] disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      ) : null}
    </div>
  );
}
